import { Controller } from "@hotwired/stimulus"

// Step 2's live preview.
//
// The generator's output is drawn client-side and stays editable until the
// organizer commits, which is what replaces the old "generate, then repair ten
// rounds one at a time" loop. Dates, training offsets and the round count all
// recompute as the controls change; names and tracks are edited in the table
// and travel to the server as generator[rounds][] overrides.
const DAY = 24 * 60 * 60 * 1000
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]
const DAYNAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]
const ORDINALS = ["first", "second", "third", "fourth", "last"]
// Monday-first ordering, so Sunday reads last in "Wed and Sun" rather than first.
const isoPos = (wday) => (wday + 6) % 7

export default class extends Controller {
  static targets = [
    "count",
    "interval",
    "time",
    "duration",
    "trainingToggle",
    "trainingWeekday",
    "trainingTime",
    "trainingCaption",
    "rows",
    "summary",
    "dlcNotice",
    "submit",
    "hoverCard",
    "customTrigger",
    "customTick",
    "customPanel",
    "customEvery",
    "unitOption",
    "weeklyRow",
    "weekdayChip",
    "weekdayNote",
    "monthlyRow",
    "monthMode",
    "monthModeLabel",
    "patternSummary",
  ]
  static values = { startsOn: String, tracks: Array }

  connect() {
    // The custom pattern is a mode, not a value: while it is on, the Repeats
    // integer is ignored and the dates come from the panel instead.
    this.custom = false
    this.unit = "week"
    this.weekdays = []
    this.monthMode = "nth"

    // Edits survive a recompute: the organizer may set a track, then change the
    // interval, and losing their work to a date change would be its own bug.
    this.overrides = new Map()
    this.render()
  }

  render() {
    const rounds = this.schedule()
    this.rowsTarget.innerHTML = rounds.map((r, i) => this.row(r, i)).join("")
    this.renderSummary(rounds)
    this.renderTrainingCaption()
    this.renderCustom(rounds)
    if (this.hasSubmitTarget) {
      this.submitTarget.value = `Create ${rounds.length} ${rounds.length === 1 ? "round" : "rounds"}`
    }
  }

  // ───── the schedule itself ─────

  schedule() {
    const count = Math.max(1, parseInt(this.countTarget.value, 10) || 1)
    const start = this.firstAt()
    if (!start) return []

    return this.patternDates(start, count).map((at, i) => {
      const o = this.overrides.get(i) || {}
      return {
        index: i,
        at,
        training: this.trainingFor(at),
        name: o.name ?? `Round ${i + 1}`,
        trackId: o.trackId ?? "",
      }
    })
  }

  // Every branch steps in calendar units, never in milliseconds: adding
  // i*interval*DAY drifts an hour once the clocks change, so a preview built in
  // September promised 19:00 races from late October while the server, which
  // uses ActiveSupport's `.days`, would have created them at 20:00.
  patternDates(start, count) {
    if (!this.custom) {
      const interval = parseInt(this.intervalTarget.value, 10) || 7
      return Array.from({ length: count }, (_, i) => {
        const at = new Date(start)
        at.setDate(at.getDate() + i * interval)
        return at
      })
    }

    const n = this.every
    if (this.unit === "day") {
      return Array.from({ length: count }, (_, i) => {
        const at = new Date(start)
        at.setDate(at.getDate() + i * n)
        return at
      })
    }

    if (this.unit === "week") {
      // Anchored on the Monday of the start week so a cycle is a whole week
      // however far into it the season begins, then walked n weeks at a time.
      const days = (this.weekdays.length ? this.weekdays : [start.getDay()])
        .slice()
        .sort((a, b) => isoPos(a) - isoPos(b))
      const anchor = new Date(start)
      anchor.setDate(anchor.getDate() - isoPos(anchor.getDay()))
      const out = []
      for (let cycle = 0; out.length < count && cycle < 400; cycle++) {
        for (const wd of days) {
          const at = new Date(anchor)
          at.setDate(at.getDate() + cycle * n * 7 + isoPos(wd))
          if (at >= start && out.length < count) out.push(at)
        }
      }
      return out.sort((a, b) => a - b)
    }

    // Monthly: either the same weekday position ("the second Sunday") or the
    // same day number, clamped so the 31st does not spill into the next month.
    const nth = Math.ceil(start.getDate() / 7)
    const wd = start.getDay()
    const out = []
    for (let i = 0; out.length < count && i < 400; i++) {
      const at = new Date(
        start.getFullYear(),
        start.getMonth() + i * n,
        1,
        start.getHours(),
        start.getMinutes(),
      )
      if (this.monthMode === "dom") {
        const last = new Date(at.getFullYear(), at.getMonth() + 1, 0).getDate()
        at.setDate(Math.min(start.getDate(), last))
      } else {
        const shift = (wd - at.getDay() + 7) % 7
        at.setDate(1 + shift + (nth - 1) * 7)
        // A fifth occurrence does not exist every month; fall back to the last.
        if (at.getMonth() !== (start.getMonth() + i * n + 12) % 12)
          at.setDate(at.getDate() - 7)
      }
      out.push(at)
    }
    return out
  }

  get every() {
    if (!this.hasCustomEveryTarget) return 1
    return Math.max(
      1,
      Math.min(52, parseInt(this.customEveryTarget.value, 10) || 1),
    )
  }

  firstAt() {
    if (!this.startsOnValue) return null
    const [y, m, d] = this.startsOnValue.split("-").map(Number)
    const [hh, mm] = (this.timeTarget.value || "20:00").split(":").map(Number)
    return new Date(y, m - 1, d, hh || 0, mm || 0)
  }

  // Mirrors GenerateRounds#compute_training_at: the most recent matching
  // weekday strictly before the race, walking back up to 8 days.
  trainingFor(raceAt) {
    if (!this.trainingOn) return null
    const weekday = parseInt(this.trainingWeekdayTarget.value, 10)
    const [hh, mm] = (this.trainingTimeTarget.value || "20:00")
      .split(":")
      .map(Number)
    const candidate = new Date(raceAt)
    candidate.setHours(hh || 0, mm || 0, 0, 0)
    for (let i = 0; i < 8; i += 1) {
      if (candidate.getDay() === weekday && candidate < raceAt)
        return new Date(candidate)
      candidate.setDate(candidate.getDate() - 1)
    }
    return null
  }

  get trainingOn() {
    return this.hasTrainingToggleTarget && this.trainingToggleTarget.checked
  }

  // ───── the custom pattern ─────

  openCustom() {
    // The pattern editor replaces the menu that launched it. Comboboxes already
    // close each other on this event, so announcing it closes the Repeats menu
    // without this controller having to reach into another controller.
    document.dispatchEvent(
      new CustomEvent("combobox:opened", { detail: { source: this } }),
    )
    this.custom = true
    // Seed the weekday chips from the start date so the panel opens describing
    // the schedule already on screen rather than an empty one.
    if (!this.weekdays.length) {
      const start = this.firstAt()
      if (start) this.weekdays = [start.getDay()]
    }
    this.render()
  }

  closeCustom() {
    this.custom = false
    this.render()
  }

  pickUnit(event) {
    this.unit = event.currentTarget.dataset.unit
    this.render()
  }

  pickMonthMode(event) {
    this.monthMode = event.currentTarget.dataset.mode
    this.render()
  }

  toggleWeekday(event) {
    const wd = Number(event.currentTarget.dataset.wday)
    const next = this.weekdays.includes(wd)
      ? this.weekdays.filter((d) => d !== wd)
      : [...this.weekdays, wd]
    // Never zero days: an empty set has no dates to draw, and the panel would
    // blank the preview rather than say anything useful.
    if (next.length) this.weekdays = next
    this.render()
  }

  renderCustom(rounds) {
    if (!this.hasCustomPanelTarget) return

    this.customPanelTarget.classList.toggle("hidden", !this.custom)
    this.customPanelTarget.classList.toggle("flex", this.custom)
    if (this.hasCustomTriggerTarget)
      this.customTriggerTarget.dataset.on = String(this.custom)
    if (this.hasCustomTickTarget)
      this.customTickTarget.classList.toggle("hidden", !this.custom)
    if (!this.custom) return

    this.unitOptionTargets.forEach((b) => {
      b.dataset.on = String(b.dataset.unit === this.unit)
    })
    this.weeklyRowTarget.classList.toggle("hidden", this.unit !== "week")
    this.monthlyRowTarget.classList.toggle("hidden", this.unit !== "month")

    this.weekdayChipTargets.forEach((b) => {
      b.dataset.on = String(this.weekdays.includes(Number(b.dataset.wday)))
    })

    const perCycle = this.weekdays.length || 1
    this.weekdayNoteTarget.textContent =
      perCycle > 1
        ? `${perCycle} races per cycle, so ${rounds.length} rounds run ${Math.ceil(rounds.length / perCycle)} cycles`
        : "One race per cycle"

    this.renderMonthModes()
    this.patternSummaryTarget.textContent = this.summaryLine(rounds)
  }

  renderMonthModes() {
    const start = this.firstAt()
    if (!start) return
    const nth = ORDINALS[Math.min(4, Math.ceil(start.getDate() / 7) - 1)]
    const labels = {
      nth: `The ${nth} ${DAYNAMES[start.getDay()]} of the month`,
      dom: `Day ${start.getDate()} of the month`,
    }
    this.monthModeTargets.forEach((b) => {
      const on = b.dataset.mode === this.monthMode
      b.dataset.on = String(on)
      b.querySelector("span span").style.opacity = on ? "1" : "0"
      b.querySelector(
        "[data-season-schedule-target='monthModeLabel']",
      ).textContent = labels[b.dataset.mode]
    })
  }

  patternLabel() {
    if (!this.custom) {
      const days = parseInt(this.intervalTarget.value, 10) || 7
      if (days === 1) return "Every day"
      return days === 7 ? "Every week" : `Every ${days / 7} weeks`
    }
    const n = this.every
    const plural = (word) => (n === 1 ? word : `${n} ${word}s`)
    if (this.unit === "day") return `Every ${plural("day")}`
    if (this.unit === "month") return `Every ${plural("month")}`

    const names = this.weekdays
      .slice()
      .sort((a, b) => isoPos(a) - isoPos(b))
      .map((d) => WEEKDAYS[d])
    const list =
      names.length < 3
        ? names.join(" and ")
        : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
    return `Every ${plural("week")} on ${list}`
  }

  summaryLine(rounds) {
    if (!rounds.length) return "Set a start date first"
    const label = this.patternLabel()
    const time = this.hasTimeTarget ? this.timeTarget.value : ""
    return (
      `${rounds.length} rounds, ${label.charAt(0).toLowerCase()}${label.slice(1)}` +
      ` at ${time} · ${this.fmt(rounds[0].at)} to ${this.fmt(rounds[rounds.length - 1].at)}`
    )
  }

  // ───── rendering ─────

  row(round, i) {
    const track = this.tracksValue.find(
      (t) => String(t.id) === String(round.trackId),
    )
    const trackCell = track
      ? `<span class="truncate">${this.escape(track.name)}</span>${track.dlc ? ' <span class="font-mono shrink-0 rounded-[3px] bg-neon-yellow-orange/18 px-[5px] py-px text-[9px] font-bold tracking-[0.06em] text-neon-yellow-orange" title="Needs add-on content">DLC</span>' : ""}`
      : '<span class="truncate text-app-text-faint">No track yet</span>'

    // The cells carry no hover fill of their own — the row owns the highlight,
    // and the affordance is the icon that fades in with it: a pencil on the
    // name (it renames in place) and a chevron on the track (it opens a menu).
    return `
      <div class="group flex items-center gap-3 border-b border-app-border-subtle px-3 py-2 last:border-b-0 transition-colors hover:bg-app-surface-hover" data-row="${i}">
        <span class="font-mono w-[26px] shrink-0 text-[11px] text-app-text-faint">R${i + 1}</span>
        <button type="button"
                data-action="season-schedule#editName mouseenter->season-schedule#showCard mouseleave->season-schedule#hideCard"
                data-card="${this.attr(this.roundCard(round))}"
                class="flex min-w-0 flex-[2] cursor-text items-center gap-1.5 rounded px-1 -mx-1 py-0.5 text-left text-[12.5px] text-app-text">
          <span class="truncate">${this.escape(round.name)}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"
               class="h-[11px] w-[11px] shrink-0 text-app-text-faint opacity-0 transition-opacity group-hover:opacity-100">
            <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
          </svg>
        </button>
        <button type="button"
                data-action="track-picker#open mouseenter->season-schedule#showCard mouseleave->season-schedule#hideCard"
                data-picker-key="${i}"
                data-card="${this.attr(this.trackCard(track))}"
                class="flex min-w-0 flex-[2] items-center gap-1.5 rounded px-1 -mx-1 py-0.5 text-left text-[12.5px] text-app-text">
          ${trackCell}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"
               class="ml-auto h-[11px] w-[11px] shrink-0 text-app-text-faint opacity-0 transition-opacity group-hover:opacity-100">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        <span class="font-mono w-[150px] shrink-0 text-right text-[11.5px] text-app-text-secondary">${this.fmt(round.at)} · ${this.time(round.at)}</span>
        <span class="font-mono w-[110px] shrink-0 text-right text-[11px] text-app-text-faint">${round.training ? this.fmt(round.training) : "-"}</span>
        <input type="hidden" name="generator[rounds][${i}][name]" value="${this.escape(round.name)}">
        <input type="hidden" name="generator[rounds][${i}][track_id]" value="${this.escape(String(round.trackId))}">
        ${this.custom ? `<input type="hidden" name="generator[dates][]" value="${round.at.toISOString()}">` : ""}
      </div>`
  }

  renderSummary(rounds) {
    if (!this.hasSummaryTarget) return
    if (rounds.length === 0) {
      this.summaryTarget.textContent = "Set a start date first"
      return
    }
    const first = rounds[0].at
    const last = rounds[rounds.length - 1].at
    this.summaryTarget.textContent = `${rounds.length} ${rounds.length === 1 ? "round" : "rounds"} · ${this.fmt(first)} to ${this.fmt(last)}`

    if (this.hasDlcNoticeTarget) {
      const dlc = rounds.filter((r) => {
        const t = this.tracksValue.find(
          (x) => String(x.id) === String(r.trackId),
        )
        return t?.dlc
      }).length
      this.dlcNoticeTarget.textContent =
        dlc > 0
          ? `${dlc} ${dlc === 1 ? "track needs" : "tracks need"} add-on content`
          : ""
      this.dlcNoticeTarget.classList.toggle("hidden", dlc === 0)
    }
  }

  renderTrainingCaption() {
    if (!this.hasTrainingCaptionTarget) return
    const rounds = this.schedule()
    const withTraining = rounds.find((r) => r.training)
    if (!this.trainingOn || !withTraining) {
      this.trainingCaptionTarget.textContent = ""
      return
    }
    const gap = Math.round((withTraining.at - withTraining.training) / DAY)
    this.trainingCaptionTarget.textContent = `${gap} ${gap === 1 ? "day" : "days"} before each race`
  }

  // ───── editing ─────

  editName(event) {
    const row = this.rowIndex(event)
    const cell = event.currentTarget
    const current = this.schedule()[row]?.name ?? ""
    // The underline is an inset shadow, not a border: a border occupies layout
    // and grew the row by 1px mid-edit, which is exactly the reflow-under-the-
    // cursor the design calls out.
    cell.outerHTML = `
      <input type="text" value="${this.escape(current)}" data-row-input="${row}"
             data-action="blur->season-schedule#commitName keydown->season-schedule#nameKey"
             style="box-shadow: inset 0 -1px 0 0 var(--color-vivid-purple);"
             class="min-w-0 flex-[2] border-0 bg-transparent px-1 -mx-1 py-0.5 text-[12.5px] text-app-text focus:outline-none focus:ring-0">`
    const input = this.element.querySelector(`[data-row-input="${row}"]`)
    input?.focus()
    // Selected, not just focused: these start as "Round 4", and renaming is
    // replacing that, not appending to it.
    input?.select()
  }

  nameKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.render()
    }
  }

  commitName(event) {
    const row = parseInt(event.currentTarget.dataset.rowInput, 10)
    this.setOverride(row, { name: event.currentTarget.value.trim() })
    this.render()
  }

  // The picker announces a choice; the row it was opened for rides along as
  // the key, because by now the table may have been redrawn and the cell that
  // opened it no longer exists.
  trackPicked(event) {
    const { trackId, key } = event.detail
    if (key === null || key === undefined) return
    this.setOverride(Number(key), { trackId })
    this.render()
  }

  setOverride(index, patch) {
    this.overrides.set(index, {
      ...(this.overrides.get(index) || {}),
      ...patch,
    })
  }

  rowIndex(event) {
    return parseInt(event.currentTarget.closest("[data-row]").dataset.row, 10)
  }

  // ───── hover cards ─────
  //
  // Both preview cells and every picker row carry one: a round's card states
  // the full date, duration and training day plus the rename affordance, and a
  // track's states country, length and — for add-on content — the pack and its
  // release date, so what a track costs is legible before it is picked.

  showCard(event) {
    const el = event.currentTarget
    const html = el.dataset.card
    if (!html || !this.hasHoverCardTarget) return

    const card = this.hoverCardTarget
    card.innerHTML = html
    card.classList.remove("hidden")

    const cr = card.getBoundingClientRect()
    const w = cr.width
    const h = cr.height

    // A preview cell's card sits above the cell when it fits, below otherwise,
    // and lines up with the cell's text rather than its padded box.
    const r = el.getBoundingClientRect()
    const above = r.top - 4 - h
    const top = above >= 8 ? above : r.bottom + 4
    card.style.left = `${Math.round(Math.max(8, Math.min(r.left - 5, window.innerWidth - w - 8)))}px`
    card.style.top = `${Math.round(Math.min(Math.max(8, top), window.innerHeight - h - 8))}px`
  }

  hideCard() {
    this.hoverCardTarget?.classList.add("hidden")
  }

  roundCard(round) {
    const duration = this.durationLabel()
    const training = round.training
      ? `Training ${this.fmt(round.training)} · click to rename`
      : "click to rename"
    return (
      '<span class="block px-3 py-2">' +
      `<span class="block whitespace-nowrap text-[12.5px] font-semibold text-app-text">${this.escape(round.name)}</span>` +
      `<span class="font-mono mt-[3px] block whitespace-nowrap text-[10.5px] text-app-text-muted">${this.fmt(round.at)} ${round.at.getFullYear()} · ${this.time(round.at)} · ${duration}</span>` +
      `<span class="mt-[5px] block whitespace-nowrap text-[10.5px] text-app-text-faint">${training}</span>` +
      "</span>"
    )
  }

  trackCard(track) {
    if (!track) {
      return '<span class="block px-3 py-2 text-[12.5px] text-app-text-faint">No track yet · click to pick one</span>'
    }
    const meta = [
      track.country,
      track.length,
      track.dlc ? "add-on content" : "base content",
    ]
      .filter(Boolean)
      .join(" · ")
    let html =
      '<span class="block px-3 py-2">' +
      `<span class="block whitespace-nowrap text-[12.5px] font-semibold text-app-text">${this.escape(track.name)}</span>` +
      `<span class="font-mono mt-[3px] block whitespace-nowrap text-[10.5px] text-app-text-muted">${this.escape(meta)}</span>`
    if (track.dlc) {
      html +=
        '<span class="mt-1.5 block border-t border-app-border-subtle pt-1.5">' +
        '<span class="flex items-center gap-1.5">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" ' +
        'class="h-3 w-3 shrink-0 text-neon-yellow-orange">' +
        '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>' +
        '<span class="whitespace-nowrap text-[11px] font-semibold text-app-text-secondary">Needs add-on content</span>' +
        "</span>" +
        `<span class="mt-1 block whitespace-nowrap text-[11px] text-app-text-secondary">${this.escape(track.pack || "")}</span>` +
        (track.released
          ? `<span class="font-mono mt-[2px] block whitespace-nowrap text-[10px] text-app-text-faint">Released ${this.escape(track.released)}</span>`
          : "") +
        "</span>"
    }
    return `${html}</span>`
  }

  durationLabel() {
    const minutes = parseInt(this.durationTarget?.value, 10) || 0
    if (minutes % 60 === 0) return `${minutes / 60} h`
    return `${minutes} min`
  }

  // ───── helpers ─────

  fmt(d) {
    return `${WEEKDAYS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`
  }
  time(d) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
  }
  // Escaped for an attribute: the card is markup, so it can't go through the
  // text escaper without being neutered.
  attr(html) {
    return html.replace(/"/g, "&quot;")
  }
  escape(s) {
    return String(s).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    )
  }
}
